import React from 'react';
import Dropdowns from '../../components/Dropdowns';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { useCustomerDataFilters } from 'contexts/CustomerDataFiltersContext';
import { fetchCustomerData, CustomerDataResponse, CustomerData } from './api';
import CustomerCard from './components/CustomerCard';
import { useSmoothLoading } from 'lib/use-smooth-loading';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { Search } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';


const CustomerDataPage: React.FC = () => {
  const lastQueryRef = React.useRef<string | null>(null);
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { years, year, levels, loading, error, handleSelectChange, handleBatchSelect, handleYearChange, getCurrentFilters } = useCustomerDataFilters();
  
  // Customer data state
  const [customerData, setCustomerData] = React.useState<CustomerData[]>([]);
  const [total, setTotal] = React.useState<number>(0);
  const [dataLoading, setDataLoading] = React.useState<boolean>(false);
  const [dataError, setDataError] = React.useState<string>('');
  const [hasFetchedOnce, setHasFetchedOnce] = React.useState(false);
  
  // UI state
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  const [searchTerm, setSearchTerm] = React.useState<string>(initialSearch);
  const [conversionType, setConversionType] = React.useState<'INR' | 'Lakhs' | 'Crores'>('Lakhs');
  const [sortBy] = React.useState<string>('total_income');
  const [sortDir] = React.useState<'asc' | 'desc'>('desc');
  const [page, setPage] = React.useState<number>(1);
  const [pageSize] = React.useState<number>(20);
  
  // Smooth loading states
  const { isLoading: smoothDataLoading, showContent: showDataContent } = useSmoothLoading(dataLoading, {
    minLoadingTime: 200,
    debounceTime: 200
  });
  


  const fetchData = React.useCallback(async (search?: string, currentPage = 1) => {
    if (!year) return;
    const filters = getCurrentFilters();

    const queryKey = JSON.stringify({
      year,
      filters,
      search: search?.trim() || '',
      page: currentPage,
      page_size: pageSize,
      sortBy,
      sortDir,
    });
  
    // Dedupe: if this exact query was just fetched, skip
    if (lastQueryRef.current === queryKey) {
      return;
    }
    lastQueryRef.current = queryKey;
    
    setDataLoading(true);
    setDataError('');
    try {
      const data = await fetchCustomerData({
        year,
        filters,
        ...(search && search.trim() && { search: search.trim() }),
        sort: {
          by: sortBy,
          dir: sortDir,
        },
        page: currentPage,
        page_size: pageSize,
      });
      setCustomerData(data.customers);
      setTotal(data.total);
    } catch (err: unknown) {
      const isForbidden = (err as any)?.status === 403;
      const message = err instanceof Error ? err.message : isForbidden ? 'You are not allowed to access this resource.' : 'Failed to fetch customer data';
      setDataError(message);
      if (isForbidden) {
        setCustomerData([]);
        setTotal(0);
      }
    } finally {
      setDataLoading(false);
      setHasFetchedOnce(true);
    }
  }, [getCurrentFilters, year, sortBy, sortDir, pageSize]);

  React.useEffect(() => {
    setBreadcrumbs([
      { label: 'Reports', href: '#' },
      { label: 'Monthly Customer Data' },
    ]);
  }, [setBreadcrumbs]);

  React.useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    setSearchTerm(urlSearch);
  }, [searchParams]);

  React.useEffect(() => {
    if (!year) return;
    setCustomerData([]);
    setTotal(0);
    setPage(1);
    // setSearchTerm(''); // Reset search when year changes
  }, [year]);


  // Listen for changes in levels to refetch data
  React.useEffect(() => {
    if (!year) return;
    if (levels.length == 0) return;
    setPage(1);
    fetchData(searchTerm || undefined, 1);
  }, [year, levels, fetchData]);

  const handleSearch = React.useCallback((search: string) => {
    setPage(1);
    fetchData(search, 1);
  }, [fetchData]);

  const debouncedSearchRef = React.useRef<NodeJS.Timeout | undefined>(undefined);

  const debouncedSearch = React.useCallback((search: string) => {
    if (debouncedSearchRef.current) {
      clearTimeout(debouncedSearchRef.current);
    }
    debouncedSearchRef.current = setTimeout(() => {
      handleSearch(search);
    }, 300);
  }, [handleSearch]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (debouncedSearchRef.current) {
        clearTimeout(debouncedSearchRef.current);
      }
    };
  }, []);

  const handlePageChange = React.useCallback((newPage: number) => {
    setPage(newPage);
    fetchData(searchTerm, newPage);
  }, [fetchData, searchTerm]);

  const handleCustomerClick = React.useCallback((customerCode: string, customerName: string) => {
    const params = new URLSearchParams();
    params.set('customerCode', customerCode);
    params.set('customerName', customerName);
    navigate(`/reports/customer-data/branches?${params.toString()}`);
  }, [navigate]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-4 sm:px-6">
      {/* Filters Section */}
      <div className="space-y-4">
        <Dropdowns
          year={year}
          years={years}
          levels={levels}
          loading={loading}
          onSelectChange={handleSelectChange}
          onBatchSelect={handleBatchSelect}
          onYearChange={handleYearChange}
        />

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}
      </div>

      {/* Search and Controls Section */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Search Bar */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search for a customer..."
            value={searchTerm}
            onChange={(e) => {
              const val = e.target.value;
              setSearchTerm(val);
              const next = new URLSearchParams(searchParams);
              if (val.trim()) {
                next.set('search', val.trim());
              } else {
                next.delete('search');
              }
              setSearchParams(next);
              debouncedSearch(val);
            }}
            className="pl-10"
          />
        </div>

        {/* Conversion Type Buttons */}
        <div className="flex gap-2">
          <button 
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              conversionType === 'INR' 
                ? 'bg-black text-white' 
                : 'bg-white border border-black hover:bg-gray-200'
            }`}
            onClick={() => setConversionType('INR')}
          >
            INR
          </button>
          <button 
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              conversionType === 'Lakhs' 
                ? 'bg-black text-white' 
                : 'bg-white border border-black hover:bg-gray-200'
            }`}
            onClick={() => setConversionType('Lakhs')}
          >
            Lakhs
          </button>
          <button 
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
              conversionType === 'Crores' 
                ? 'bg-black text-white' 
                : 'bg-white border border-black hover:bg-gray-200'
            }`}
            onClick={() => setConversionType('Crores')}
          >
            Crores
          </button>
        </div>
      </div>

      {/* Data Section */}
      {dataError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600 text-sm">{dataError}</p>
        </div>
      )}

      {smoothDataLoading && (
        <div className="space-y-6">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="bg-white rounded-lg border shadow-sm">
              <div className="bg-gray-200 animate-pulse h-16 rounded-t-lg" />
              <div className="p-6 space-y-4">
                <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4" />
                <div className="h-4 bg-gray-200 animate-pulse rounded w-1/2" />
                <div className="h-4 bg-gray-200 animate-pulse rounded w-2/3" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!smoothDataLoading && hasFetchedOnce && customerData.length === 0 && !dataError && (
        <div className="text-center py-12">
          <p className="text-gray-500">No customer data found. Try adjusting your filters or search terms.</p>
        </div>
      )}

      {!smoothDataLoading && customerData.length > 0 && showDataContent && (
        <div className={`space-y-6 transition-opacity duration-300 ${showDataContent ? 'opacity-100' : 'opacity-0'}`}>
          {/* Results Summary */}
          <div className="text-sm text-gray-600">
            Showing {customerData.length} of {total} customers
            {searchTerm && ` for "${searchTerm}"`}
          </div>

          {/* Customer Cards */}
          <div className="space-y-6">
            {customerData.map((customer) => (
              <CustomerCard
                key={customer.customer_code}
                customer={customer}
                conversionType={conversionType}
                onClick={handleCustomerClick}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
              >
                Previous
              </Button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={page === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerDataPage;
